import {
  compareSourceKeys,
  formatBytes,
  formatDuration,
  formatTimecode,
  recordingOffset,
  type BackupFile,
  type RecordingDetail,
  type Source,
  type SourceMedia,
  type Take,
  type UploadProgress,
} from '@greenroom/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { ArrowLeft, CloudUpload, Download, Ellipsis, FileSpreadsheet, FolderDown, Mic, Monitor, Pencil, RotateCw, Scissors, Trash2, Video } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router'
import { TransferDialog } from '../components/transfer-dialog'
import { EventList } from '../components/event-log'
import { MarkerDot } from '../components/marker-dot'
import { MediaStatusBadge, RecordingStatusBadge, recordingTitle } from '../components/recording-list'
import { AnchorButton, Button, LinkButton } from '../components/ui/button'
import { ConfirmDialog, Dialog } from '../components/ui/dialog'
import { Field } from '../components/ui/field'
import { Menu, MenuItem, MenuSeparator } from '../components/ui/menu'
import { Badge, PageHeader, Section } from '../components/ui/misc'
import { FullPageSpinner } from '../components/ui/spinner'
import { Tooltip } from '../components/ui/tooltip'
import { api } from '../lib/api'
import { formatDateTime, formatRelative, listNames, plural } from '../lib/format'
import { q } from '../lib/queries'
import { useTitle } from '../lib/title'
import { QueryError } from './errors'

export function RecordingPage() {
  const { recordingId = '' } = useParams()
  const recording = useQuery(q.recording(recordingId))
  useTitle(recording.data && recordingTitle(recording.data), 'Recordings')
  if (recording.isPending) return <FullPageSpinner />
  if (recording.isError) return <QueryError error={recording.error} retry={recording.refetch} what="recording" />
  return <RecordingView recording={recording.data} />
}

const SOURCE_ICON = { camera: Video, mic: Mic, screen: Monitor }

function RecordingView({ recording: r }: { recording: RecordingDetail }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<'rename' | 'delete' | 'process' | 'move' | 'duplicate' | null>(null)
  const unplaced = r.import?.status === 'draft' || r.import?.files.some((f) => f.role === 'source' && !f.alignment)
  const ended = r.status === 'ended'
  const waiting = r.media.some((m) => m.status === 'waiting')
  const failed = r.media.some((m) => m.status === 'failed')

  const openEditor = useMutation({
    mutationFn: () => api<{ id: string }>(`/api/recordings/${r.id}/project`, { method: 'POST' }),
    onSuccess: () => navigate(`/recordings/${r.id}/edit`),
  })
  const process = useMutation({
    mutationFn: () => api(`/api/recordings/${r.id}/process`, { method: 'POST' }),
    meta: { silent: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: q.recording(r.id).queryKey }),
  })
  const remove = useMutation({
    mutationFn: () => api(`/api/recordings/${r.id}`, { method: 'DELETE' }),
    meta: { silent: true },
    onSuccess: () => {
      qc.removeQueries({ queryKey: q.recording(r.id).queryKey })
      qc.setQueriesData<{ id: string }[]>({ queryKey: ['teams', r.teamId, 'recordings'] }, (list) => list?.filter((x) => x.id !== r.id))
      navigate('/recordings', { replace: true })
    },
  })

  const people = [...new Set(r.sources.map((s) => s.participantId))].map((id) => ({
    id,
    name: r.sources.find((s) => s.participantId === id)!.participantName,
    sources: r.sources.filter((s) => s.participantId === id).sort(bySourceKey),
  }))

  return (
    <>
      {(dialog === 'move' || dialog === 'duplicate') && <TransferDialog recording={r} kind={dialog} onClose={() => setDialog(null)} />}
      <PageHeader
        back={
          <LinkButton to="/recordings" variant="ghost" size="sm" icon={<ArrowLeft className="size-4" />} className="-ml-3">
            Recordings
          </LinkButton>
        }
        title={recordingTitle(r)}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <RecordingStatusBadge recording={r} />
            <span>
              {r.studioName} · {formatDateTime(r.startedAt)} · {formatDuration(r.durationMs)}
              {r.takes.length > 1 && ` in ${plural(r.takes.length, 'take')}`}
            </span>
          </span>
        }
        actions={
          <>
            {(r.canEdit || r.projectId) && (
              <Button variant="primary" icon={<Scissors className="size-4" />} disabled={!ended || unplaced} loading={openEditor.isPending} onClick={() => openEditor.mutate()}>
                {r.canEdit ? 'Edit' : 'Open edit'}
              </Button>
            )}
            <Menu
              trigger={
                <Button icon={<Download className="size-4" />} disabled={!ended}>
                  Download
                </Button>
              }
            >
              <MenuItem icon={<FolderDown />} onSelect={() => window.location.assign(`/api/recordings/${r.id}/export.zip`)}>
                All tracks (.zip)
              </MenuItem>
              <MenuItem icon={<FolderDown />} onSelect={() => window.location.assign(`/api/recordings/${r.id}/originals.zip`)}>Original files (.zip)</MenuItem>
              <MenuItem icon={<FileSpreadsheet />} onSelect={() => window.location.assign(`/api/recordings/${r.id}/markers.csv`)}>
                Markers (.csv)
              </MenuItem>
            </Menu>
            {r.canEdit && (
              <Menu
                trigger={
                  <Button size="icon" aria-label="More">
                    <Ellipsis className="size-4" />
                  </Button>
                }
              >
                <MenuItem icon={<Pencil />} onSelect={() => setDialog('rename')}>
                  Rename
                </MenuItem>
                <MenuItem icon={<RotateCw />} disabled={!ended || (!waiting && !failed && !(r.import?.status === 'committed' && unplaced))} onSelect={() => setDialog('process')}>
                  {failed || r.import?.status === 'committed' && unplaced ? 'Process now' : 'Process without missing uploads'}
                </MenuItem>
                {(['move', 'duplicate'] as const).map((kind) => <Tooltip key={kind} label={r.transferBusyReason ? `Recording is busy: ${r.transferBusyReason}` : kind === 'move' ? 'Move recording' : 'Duplicate recording'}><span className="inline-flex w-full"><MenuItem disabled={!!r.transferBusyReason} onSelect={() => setDialog(kind)}>{kind === 'move' ? 'Move to…' : 'Duplicate to…'}</MenuItem></span></Tooltip>)}
                <MenuSeparator />
                <MenuItem icon={<Trash2 />} danger disabled={!ended} onSelect={() => setDialog('delete')}>
                  Delete recording
                </MenuItem>
              </Menu>
            )}
          </>
        }
      />

      {r.import && <Section title={unplaced ? 'Placing imported tracks' : r.mediaStatus === 'processing' ? 'Processing imported tracks' : 'Imported recording'}>
        {r.canEdit && <LinkButton to={`/recordings/import?importId=${r.import.id}&studioId=${r.studioId}`}>{r.import.status === 'draft' ? 'Continue import' : 'Review mapping'}</LinkButton>}
        {r.import.error && <p className="text-danger">{r.import.error}</p>}
        {r.import.files.filter((f) => f.alignment?.needsReview || f.status === 'failed').map((f) => <div key={f.id} className="my-3 space-y-2"><p>{f.participantName} · {f.name}: {f.error ?? 'Review timing'}</p>{r.canEdit && <OffsetField fileId={f.id} offsetMs={f.offsetMs ?? f.alignment?.offsetMs ?? 0} recordingId={r.id} />}</div>)}
      </Section>}
      {!ended && (
        <Banner tone="rec">
          This session is still {r.status === 'paused' ? 'paused' : 'recording'}. Tracks upload while it runs and are processed once a producer stops it.
        </Banner>
      )}
      {ended && waiting && (
        <Banner tone="warn">
          Waiting for uploads from {listNames(uploadingNames(r))}. Each participant uploads their own tracks; this finishes when their browser has sent everything,
          even after they reconnect later.
        </Banner>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          {people.length === 0 ? (
            <Section title="Tracks">
              <p className="text-sm text-muted">No tracks arrived for this recording.</p>
            </Section>
          ) : (
            people.map((p) => (
              <Section key={p.id} title={p.name}>
                <div className="space-y-4">
                  {p.sources.map((s) => (
                    <SourceRow key={s.id} source={s} recording={r} />
                  ))}
                </div>
              </Section>
            ))
          )}
          {ended && <Backups recordingId={r.id} />}
        </div>
        <div className="space-y-6">
          <Section title="Markers">
            <MarkerList recording={r} />
          </Section>
          <Section title="Session log">
            <EventList events={r.events} empty="No events were logged." />
          </Section>
        </div>
      </div>

      <RenameDialog recording={r} open={dialog === 'rename'} onOpenChange={(o) => setDialog(o ? 'rename' : null)} />
      <ConfirmDialog
        open={dialog === 'process'}
        onOpenChange={(o) => setDialog(o ? 'process' : null)}
        title="Process without the missing uploads?"
        description="Tracks are assembled from what has arrived so far. Gaps stay silent or black. Uploads that still come in later are added automatically."
        confirmLabel="Process now"
        onConfirm={() => process.mutateAsync()}
      />
      <ConfirmDialog
        open={dialog === 'delete'}
        onOpenChange={(o) => setDialog(o ? 'delete' : null)}
        title="Delete this recording?"
        description="All tracks, the edit and its renders are deleted for everyone. This cannot be undone."
        confirmLabel="Delete for everyone"
        destructive
        onConfirm={() => remove.mutateAsync()}
      />
    </>
  )
}

const bySourceKey = (a: Source, b: Source) => compareSourceKeys(a.sourceKey, b.sourceKey)

function uploadingNames(r: RecordingDetail): string[] {
  const ids = new Set(r.media.filter((m) => m.status === 'waiting').map((m) => r.sources.find((s) => s.id === m.sourceId)?.participantName))
  return [...ids].filter((n): n is string => Boolean(n))
}

function Banner({ tone, children }: { tone: 'warn' | 'rec'; children: React.ReactNode }) {
  return (
    <div className={clsx('mb-6 flex items-start gap-3 rounded-xl px-4 py-3 text-sm', tone === 'rec' ? 'bg-rec-soft text-text' : 'bg-warn-soft text-text')}>
      <CloudUpload className={clsx('mt-0.5 size-4 shrink-0', tone === 'rec' ? 'text-rec' : 'text-warn')} />
      <p>{children}</p>
    </div>
  )
}

function SourceRow({ source, recording: r }: { source: Source; recording: RecordingDetail }) {
  const { data: transcripts = [] } = useQuery(q.transcripts(r.id))
  const transcript = transcripts.find((t) => t.sourceId === source.id)
  const Icon = SOURCE_ICON[source.kind]
  const label = source.sourceKey === 'mix' ? 'Mix (all participants)' : source.kind === 'camera' ? 'Camera' : source.kind === 'mic' ? 'Microphone (lossless)' : `Screen ${source.sourceKey.slice('screen-'.length)}`
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-sm font-medium">
        <Icon className="size-4 text-muted" />
        {label}
      </div>
      {transcript && <p className="mb-2 text-xs text-muted">Transcript v{transcript.version} · {transcript.language} · {transcript.model}</p>}
      <ul className="space-y-1.5">
        {r.takes.map((t) => {
          const media = r.media.find((m) => m.sourceId === source.id && m.takeId === t.id)
          const upload = r.uploads.find((u) => u.takeId === t.id && u.participantId === source.participantId && u.sourceKey === source.sourceKey)
          if (!media && !upload) return null
          return <TakeRow key={t.id} take={t} media={media} upload={upload} multiTake={r.takes.length > 1} />
        })}
      </ul>
    </div>
  )
}

function TakeRow({ take, media, upload, multiTake }: { take: Take; media?: SourceMedia; upload?: UploadProgress; multiTake: boolean }) {
  const status = media?.status ?? 'waiting'
  const download = media?.urls.master ?? media?.urls.audio
  const coverage = media && media.durationMs && media.availability.length > 0 ? media.availability.reduce((n, [a, b]) => n + b - a, 0) / take.durationMs : null
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg bg-surface-2 px-3 py-2 text-sm">
      {multiTake && <span className="w-14 shrink-0 text-muted">Take {take.index}</span>}
      <MediaStatusBadge status={status} />
      <span className="min-w-0 flex-1 text-muted">
        {status === 'waiting' && upload ? (
          <UploadState upload={upload} />
        ) : media?.durationMs ? (
          <>
            {formatDuration(media.durationMs)}
            {media.width && media.height && ` · ${media.width}×${media.height}`}
            {coverage !== null && coverage < 0.98 && ` · on screen ${Math.round(coverage * 100)} % of the take`}
          </>
        ) : (
          media?.error && <span className="text-danger">{media.error}</span>
        )}
      </span>
      {status === 'partial' && media?.error && (
        <Tooltip label={media.error}>
          <span className="text-xs text-warn underline decoration-dotted">Gaps</span>
        </Tooltip>
      )}
      {download && (
        <Tooltip label="Download">
          <AnchorButton href={`${download}&download=1`} variant="ghost" size="icon" aria-label="Download track" download>
            <Download className="size-4" />
          </AnchorButton>
        </Tooltip>
      )}
    </li>
  )
}

function UploadState({ upload: u }: { upload: UploadProgress }) {
  const done = u.expectedChunks !== null && u.finalizedTracks === u.tracks
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {done && u.expectedChunks ? (
        <progress className="progress w-32" max={u.expectedChunks} value={u.receivedChunks} aria-label="Upload progress" />
      ) : (
        <span>uploading while recording</span>
      )}
      <span>
        {formatBytes(u.bytes)}
        {u.lastChunkAt && `, last data ${formatRelative(u.lastChunkAt)}`}
      </span>
    </span>
  )
}

function MarkerList({ recording: r }: { recording: RecordingDetail }) {
  if (r.markers.length === 0) return <p className="text-sm text-muted">Nobody set a marker. Press M in the studio to mark a moment.</p>
  return (
    <ol className="space-y-2.5">
      {r.markers.map((m) => {
        const at = recordingOffset(r.takes, m.takeId, m.at)
        return (
          <li key={m.id} className="flex items-start gap-3 text-sm">
            <MarkerDot color={m.color} className="mt-1.5" />
            <span className="min-w-0 flex-1">
              <span className="block">{m.label || <span className="text-muted">Marker</span>}</span>
              <span className="text-xs text-faint">{m.authorName}</span>
            </span>
            <span className="shrink-0 font-mono text-xs text-muted tabular-nums">{at === null ? '–' : formatTimecode(at).slice(0, 8)}</span>
          </li>
        )
      })}
    </ol>
  )
}

function Backups({ recordingId }: { recordingId: string }) {
  const backups = useQuery(q.backups(recordingId))
  if (!backups.data || backups.data.length === 0) return null
  return (
    <Section title="Cloud backup">
      <p className="mb-3 text-sm text-muted">What the server received during the call. Use it only when a participant's own tracks are missing.</p>
      <ul className="space-y-1.5">
        {backups.data.map((b: BackupFile) => (
          <li key={b.id} className="flex items-center gap-3 rounded-lg bg-surface-2 px-3 py-2 text-sm">
            <span className="min-w-0 flex-1 truncate">
              {b.participantName} · take {b.takeIndex}
            </span>
            {b.status === 'complete' ? (
              <Badge tone="ok">Ready</Badge>
            ) : b.status === 'active' ? (
              <Badge tone="accent">Recording</Badge>
            ) : (
              <Tooltip label={b.error ?? 'Failed'}>
                <Badge tone="danger">Failed</Badge>
              </Tooltip>
            )}
            {b.downloadUrl && (
              <AnchorButton href={b.downloadUrl} variant="ghost" size="icon" aria-label="Download backup" download>
                <Download className="size-4" />
              </AnchorButton>
            )}
          </li>
        ))}
      </ul>
    </Section>
  )
}

function RenameDialog({ recording, open, onOpenChange }: { recording: RecordingDetail; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient()
  const [title, setTitle] = useState(recording.title)
  const rename = useMutation({
    mutationFn: (value: string) => api<RecordingDetail>(`/api/recordings/${recording.id}`, { method: 'PATCH', body: { title: value } }),
    onSuccess: (updated) => {
      qc.setQueryData(q.recording(recording.id).queryKey, updated)
      qc.setQueriesData<{ id: string; title: string }[]>({ queryKey: ['teams', recording.teamId, 'recordings'] }, (list) =>
        list?.map((x) => (x.id === updated.id ? { ...x, title: updated.title } : x)),
      )
      onOpenChange(false)
    },
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (title.trim()) rename.mutate(title.trim())
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (o) setTitle(recording.title)
      }}
      title="Rename recording"
    >
      <form onSubmit={submit} className="space-y-6">
        <Field label="Title" placeholder={`${recording.studioName} session`} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} autoFocus required />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={rename.isPending} disabled={!title.trim()}>
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function OffsetField({ fileId, offsetMs, recordingId }: { fileId: string; offsetMs: number; recordingId: string }) {
  const [value, setValue] = useState(String(offsetMs))
  const qc = useQueryClient()
  const save = useMutation({ mutationFn: () => api(`/api/import-files/${fileId}`, { method: 'PATCH', body: { offsetMs: Number(value) } }), onSuccess: () => qc.invalidateQueries({ queryKey: q.recording(recordingId).queryKey }) })
  return <div className="flex items-end gap-2"><Field label="Offset (ms)" type="number" value={value} onChange={(e) => setValue(e.target.value)} /><Button disabled={!value || !Number.isFinite(Number(value))} loading={save.isPending} onClick={() => save.mutate()}>Apply offset</Button></div>
}
